import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import { IncidentsControlBar } from './IncidentsControlBar';
import IncidentsTable from './IncidentsTable';
import type { SortDir } from './IncidentsTable';
import CreateIncidentDrawer from './CreateIncidentDrawer';
import IncidentDetail from '../IncidentDetail/IncidentDetail';
import { useCANvisionContext } from '../../context/useCANvisionContext';
import { useIncidents } from '../../hooks/useIncidents';
import type { Incident, IncidentStatus, VMSCamera } from '../../types/canvision.types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface IncidentsTabProps {
  /** Available cameras for the camera filter dropdown */
  cameras?: VMSCamera[];
  /** Site IDs in scope (used downstream by IncidentsTable and CreateIncidentDrawer) */
  siteIds?: number[];
  /** Callback to trigger data refresh from the parent */
  onRefresh?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Top-level wrapper for the Incidents tab panel.
 *
 * Responsibilities:
 *  - Owns filter state: severity, status, camera, search text
 *  - Owns expanded-row state: expandedIncidentId
 *  - Owns create-drawer/dialog state: showCreateIncident
 *  - Renders IncidentsControlBar (filters/actions)
 *  - Renders CreateIncidentDrawer (SwipeableDrawer on desktop, Dialog on mobile)
 *  - Renders IncidentsTable with sort, expand/collapse, and infinite scroll
 */
export function IncidentsTab({
  cameras = [],
  siteIds = [],
  onRefresh,
}: IncidentsTabProps) {
  // -----------------------------------------------------------------------
  // Filter state
  // -----------------------------------------------------------------------

  const [severity, setSeverity] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [selectedCamera, setSelectedCamera] = useState<VMSCamera | null>(null);
  // -----------------------------------------------------------------------
  // Expanded row state (single-expand; null = none)
  // -----------------------------------------------------------------------

  const [expandedIncidentId, setExpandedIncidentId] = useState<number | null>(null);

  const handleToggleExpand = useCallback((id: number) => {
    setExpandedIncidentId((prev) => (prev === id ? null : id));
  }, []);

  // -----------------------------------------------------------------------
  // Create incident dialog state
  // -----------------------------------------------------------------------

  const [showCreateIncident, setShowCreateIncident] = useState(false);

  const handleOpenCreate = useCallback(() => {
    setShowCreateIncident(true);
  }, []);

  const handleCloseCreate = useCallback(() => {
    setShowCreateIncident(false);
  }, []);

  // -----------------------------------------------------------------------
  // Incident detail drawer state
  // -----------------------------------------------------------------------

  const [selectedIncidentId, setSelectedIncidentId] = useState<number | null>(null);
  const [incidentDetailOpen, setIncidentDetailOpen] = useState(false);

  const handleViewIncident = useCallback((incident: Incident) => {
    setSelectedIncidentId(incident.id);
    setIncidentDetailOpen(true);
  }, []);

  const handleCloseIncidentDetail = useCallback(() => {
    setIncidentDetailOpen(false);
    setSelectedIncidentId(null);
  }, []);

  // -----------------------------------------------------------------------
  // Sort state
  // -----------------------------------------------------------------------

  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSortChange = useCallback((property: string) => {
    setSortDir((prev) => (sortBy === property && prev === 'asc' ? 'desc' : 'asc'));
    setSortBy(property);
  }, [sortBy]);

  // -----------------------------------------------------------------------
  // Data fetching
  // -----------------------------------------------------------------------

  const incidentFilters = useMemo(() => ({
    severity: severity || undefined,
    status: status || undefined,
    cameraId: selectedCamera?.id ?? undefined,
    siteId: siteIds.length === 1 ? siteIds[0] : undefined,
  }), [severity, status, selectedCamera, siteIds]);

  const {
    incidents,
    total,
    loading: incidentsLoading,
    refetch: refetchIncidents,
    updateStatus,
  } = useIncidents(incidentFilters);

  // -----------------------------------------------------------------------
  // Notification provider (user feedback for status actions)
  // -----------------------------------------------------------------------

  const { notifySuccess, notifyError } = useNotificationProvider();

  // -----------------------------------------------------------------------
  // Inline status-change handler (from accordion row action buttons)
  // -----------------------------------------------------------------------

  const handleStatusChange = useCallback(
    async (incident: Incident, newStatus: IncidentStatus) => {
      try {
        await updateStatus(incident.id, newStatus);
        notifySuccess(`Incident status updated to ${newStatus}`);
      } catch (err) {
        // Error notification is already dispatched by useIncidents.updateStatus;
        // only log here to avoid duplicate toasts.
        console.error('[IncidentsTab] Failed to update incident status:', err);
      }
    },
    [updateStatus, notifySuccess],
  );

  // -----------------------------------------------------------------------
  // Sound toggle (delegated to SoundManager via context)
  // -----------------------------------------------------------------------

  const { isSoundMuted, toggleSoundMute, lastIncident, lastIncidentUpdate, filterByCameraId, setFilterByCameraId, setHighlightedCameraId, setChecklistMappingOpen } = useCANvisionContext();

  // -----------------------------------------------------------------------
  // Cross-section linking: apply filterByCameraId from Attention Feed
  // -----------------------------------------------------------------------

  // When filterByCameraId changes, sync it to the selectedCamera filter
  useEffect(() => {
    if (filterByCameraId) {
      const match = cameras.find((c) => c.id === filterByCameraId);
      if (match) {
        setSelectedCamera(match);
      }
    }
  }, [filterByCameraId, cameras]);

  // Derive camera name for filter chip display
  const filterCameraName = useMemo(() => {
    if (!filterByCameraId) return null;
    const match = cameras.find((c) => c.id === filterByCameraId);
    if (match) return match.display_name || match.name || `Camera ${filterByCameraId}`;
    return `Camera ${filterByCameraId}`;
  }, [filterByCameraId, cameras]);

  /** Clear the camera filter set from the Attention Feed */
  const handleClearCameraFilter = useCallback(() => {
    setFilterByCameraId(null);
    setSelectedCamera(null);
  }, [setFilterByCameraId]);

  // -----------------------------------------------------------------------
  // Auto-refetch on new socket incident (canvms:incident-created)
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (lastIncident) {
      onRefresh?.();
    }
  }, [lastIncident, onRefresh]);

  // -----------------------------------------------------------------------
  // Auto-refetch on incident status update (canvms:incident-updated)
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (lastIncidentUpdate) {
      refetchIncidents();
    }
  }, [lastIncidentUpdate, refetchIncidents]);

  // -----------------------------------------------------------------------
  // Refresh handler
  // -----------------------------------------------------------------------

  const handleRefresh = useCallback(() => {
    refetchIncidents();
    onRefresh?.();
  }, [refetchIncidents, onRefresh]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Control bar -- filters and actions */}
      <IncidentsControlBar
        severity={severity}
        onSeverityChange={setSeverity}
        status={status}
        onStatusChange={setStatus}
        selectedCamera={selectedCamera}
        onCameraChange={setSelectedCamera}
        cameras={cameras}
        soundEnabled={!isSoundMuted}
        onToggleSound={toggleSoundMute}
        onRefresh={handleRefresh}
        onCreateIncident={handleOpenCreate}
        onChecklistMapping={() => setChecklistMappingOpen(true)}
      />

      {/* "Filtered by camera" chip -- shown when Attention Feed tile was clicked */}
      {filterByCameraId && filterCameraName && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5 }}>
          <Chip
            icon={<FilterListIcon />}
            label={`Filtered by: ${filterCameraName}`}
            color="info"
            variant="outlined"
            size="small"
            onDelete={handleClearCameraFilter}
            sx={{ fontWeight: 500 }}
          />
        </Box>
      )}

      {/* Incidents table with sort, expand/collapse, infinite scroll */}
      <IncidentsTable
        incidents={incidents}
        loading={incidentsLoading}
        hasMore={incidents.length < total}
        onLoadMore={refetchIncidents}
        expandedId={expandedIncidentId}
        onExpandToggle={handleToggleExpand}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={handleSortChange}
        totalCount={total}
        onViewIncident={handleViewIncident}
        onStatusChange={handleStatusChange}
      />

      {/* Create Incident drawer (desktop) / dialog (mobile) */}
      <CreateIncidentDrawer
        open={showCreateIncident}
        onClose={handleCloseCreate}
        onCreated={() => { handleCloseCreate(); handleRefresh(); }}
        siteIds={siteIds}
        cameras={cameras}
      />

      {/* Incident detail drawer (right-side panel) */}
      <IncidentDetail
        incidentId={selectedIncidentId}
        open={incidentDetailOpen}
        onClose={handleCloseIncidentDetail}
        onStatusChange={handleRefresh}
      />

    </Box>
  );
}

export default IncidentsTab;
